import type { CSSProperties } from 'react';

import { usePlatformImage, type PlatformImageProps } from '../contexts';

export type CDNImageProps = Omit<PlatformImageProps, 'src'> & {
  src: string;
  /** CDN image resizing server URL (e.g., https://cdn.example.com) */
  cdnBaseUrl?: string;
  /** Backend URL for relative paths (e.g., https://api.example.com) */
  backendUrl?: string;
  /** Quality for CDN resize (1-100) */
  quality?: number;
  /** Width hint for CDN resize (defaults to 800 for non-Next.js) */
  cdnWidth?: number;
};

/**
 * Builds a CDN-proxied image URL for resizing/caching.
 * If cdnBaseUrl is not provided, returns the original src.
 */
function buildCdnUrl(
  src: string,
  options: {
    cdnBaseUrl?: string;
    backendUrl?: string;
    width?: number;
    quality?: number;
  },
): string {
  const { cdnBaseUrl, backendUrl, width = 800, quality } = options;

  // No CDN configured, return original
  if (!cdnBaseUrl) return src;

  let fullPathSrc = src;

  // Handle relative paths
  if (src.startsWith('/')) {
    // If no backend URL or localhost, don't proxy
    if (!backendUrl || backendUrl.startsWith('http://localhost')) {
      return src;
    }
    fullPathSrc = backendUrl + src;
  }

  // Build CDN URL
  const qualityPart = quality ? `/quality:${quality}` : '';
  const options_str = `/size:${width}:::${qualityPart}/plain/${encodeURIComponent(fullPathSrc)}`;
  return `${cdnBaseUrl}/insecure${options_str}`;
}

/**
 * CDNImage - Platform-agnostic image component with optional CDN resizing.
 *
 * In Next.js environments (with PlatformProvider), uses next/image.
 * In other environments, falls back to a regular <img> tag.
 *
 * Pass cdnBaseUrl and backendUrl to enable CDN image resizing.
 */
export function CDNImage({
  src,
  alt,
  cdnBaseUrl,
  backendUrl,
  quality,
  cdnWidth,
  fill,
  style,
  ...props
}: CDNImageProps) {
  const Image = usePlatformImage();

  // Build CDN URL if configured
  const imageSrc = buildCdnUrl(src, {
    cdnBaseUrl,
    backendUrl,
    width: cdnWidth,
    quality,
  });

  // Handle fill mode style for pure img fallback
  const fillStyle: CSSProperties | undefined = fill
    ? { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', ...style }
    : style;

  return <Image src={imageSrc} alt={alt} fill={fill} style={fillStyle} {...props} />;
}
